'use client';

import { useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface CaseStudyModalProps {
  project: {
    id: number;
    title: string;
    category: string;
    year: number;
    image: string;
    alt: string;
    description: string;
    client: string;
    challenge: string;
    solution: string;
    results?: string[];
    additionalImages?: Array<{ url: string; alt: string }>;
  } | null;
  onClose: () => void;
}

const CaseStudyModal = ({ project, onClose }: CaseStudyModalProps) => {
  useEffect(() => {
    if (project) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [project]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  if (!project) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-primary/95 overflow-y-auto"
      onClick={onClose}
    >
      <div className="min-h-screen px-6 py-20 lg:px-12 lg:py-24">
        <div
          className="max-w-5xl mx-auto bg-secondary"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="fixed top-8 right-8 p-3 bg-secondary text-primary border border-border hover:bg-muted transition-colors duration-fast z-10"
            aria-label="Close case study"
          >
            <Icon name="XMarkIcon" size={24} />
          </button>

          {/* Hero Image */}
          <div className="relative w-full aspect-[16/9] bg-card overflow-hidden">
            <AppImage
              src={project.image}
              alt={project.alt}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Content */}
          <div className="p-8 lg:p-16">
            {/* Header */}
            <div className="mb-12 pb-8 border-b border-border">
              <p className="text-xs font-cta font-semibold tracking-wide text-muted-foreground mb-4">
                {project.category} • {project.year}
              </p>
              <h2 className="text-4xl lg:text-5xl font-headline font-bold text-primary mb-6">
                {project.title}
              </h2>
              <p className="text-lg font-body text-text-secondary leading-relaxed">
                {project.description}
              </p>
            </div>

            {/* Client Info */}
            <div className="mb-12">
              <h3 className="text-xs font-cta font-semibold tracking-wide text-muted-foreground mb-2">
                CLIENT
              </h3>
              <p className="text-xl font-headline font-bold text-primary">
                {project.client}
              </p>
            </div>

            {/* Challenge */}
            <div className="mb-12">
              <h3 className="text-2xl font-headline font-bold text-primary mb-4">
                The Challenge
              </h3>
              <p className="text-base font-body text-text-secondary leading-relaxed">
                {project.challenge}
              </p>
            </div>

            {/* Solution */}
            <div className="mb-12">
              <h3 className="text-2xl font-headline font-bold text-primary mb-4">
                Our Solution
              </h3>
              <p className="text-base font-body text-text-secondary leading-relaxed">
                {project.solution}
              </p>
            </div>

            {/* Results */}
            {project.results && project.results.length > 0 && (
              <div className="mb-12">
                <h3 className="text-2xl font-headline font-bold text-primary mb-6">
                  Results & Impact
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {project.results.map((result, index) => (
                    <div
                      key={index}
                      className="p-6 bg-muted border border-border"
                    >
                      <p className="text-base font-body text-primary">
                        {result}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Images */}
            {project.additionalImages && project.additionalImages.length > 0 && (
              <div className="space-y-8">
                {project.additionalImages.map((img, index) => (
                  <div
                    key={index}
                    className="relative w-full aspect-[16/9] bg-card overflow-hidden"
                  >
                    <AppImage
                      src={img.url}
                      alt={img.alt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseStudyModal;